import axios from 'axios';

// API base URL resolution order:
//   1. REACT_APP_API_URL from the build environment (if provided).
//   2. Dev fallback: http://localhost:10000/api (local development only).
//   3. Production fallback: same origin as the page (https://<host>/api).
// The deployed nginx proxies /api/ + /socket.io/ to the backend, so a build
// made without REACT_APP_API_URL still works and can never produce mixed
// content or ERR_CONNECTION_REFUSED against the user's own machine.
const isLocalDev =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const configuredApiUrl =
  process.env.REACT_APP_API_URL ||
  (isLocalDev ? 'http://localhost:10000/api' : `${window.location.protocol}//${window.location.host}/api`);

// Root-cause fix for mixed content: never issue plain HTTP requests from an
// HTTPS page. Upgrade any http:// API URL to https:// when the page is secure.
export const API_URL = configuredApiUrl.replace(
  /^http:\/\//,
  typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'https://' : 'http://'
);

// Socket.IO runs on the same host as the REST API (nginx proxies /socket.io/).
export const SOCKET_BASE_URL = API_URL.replace(/\/api\/?$/, '');

export const TOKEN_KEY = 'token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const SESSION_ID_KEY = 'session_id';
export const REMEMBER_KEY = 'ani_remember_me';

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY),
  set: (token: string, remember: boolean) => {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    if (remember) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(REMEMBER_KEY, 'true');
    } else {
      sessionStorage.setItem(TOKEN_KEY, token);
      localStorage.removeItem(REMEMBER_KEY);
    }
  },
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY),
  setRefreshToken: (refreshToken: string, remember: boolean) => {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    (remember ? localStorage : sessionStorage).setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  getSessionId: () => localStorage.getItem(SESSION_ID_KEY) || sessionStorage.getItem(SESSION_ID_KEY),
  setSessionId: (sessionId: string, remember: boolean) => {
    localStorage.removeItem(SESSION_ID_KEY);
    sessionStorage.removeItem(SESSION_ID_KEY);
    (remember ? localStorage : sessionStorage).setItem(SESSION_ID_KEY, sessionId);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(SESSION_ID_KEY);
    sessionStorage.removeItem(SESSION_ID_KEY);
    localStorage.removeItem(REMEMBER_KEY);
  },
};

// If a refresh is already in flight, other 401s wait for the same promise
// instead of hammering /auth/refresh (single-flight).
let refreshPromise: Promise<boolean> | null = null;

const clearAuthAndNotify = () => {
  tokenStore.clear();
  window.dispatchEvent(new Event('auth-unauthorized'));
};

const tryRefreshToken = async (): Promise<boolean> => {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return false;

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
        const { token, refreshToken: newRefreshToken, sessionId } = response.data;
        const remember = localStorage.getItem(REMEMBER_KEY) === 'true';
        tokenStore.set(token, remember);
        if (newRefreshToken) tokenStore.setRefreshToken(newRefreshToken, remember);
        if (sessionId) tokenStore.setSessionId(sessionId, remember);
        return true;
      } catch (err) {
        clearAuthAndNotify();
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
};

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Interceptor to attach the JWT token to requests
api.interceptors.request.use(
  (config) => {
    const token = tokenStore.get();
    if (token) {
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set('Authorization', `Bearer ${token}`);
      } else {
        config.headers = { ...config.headers, Authorization: `Bearer ${token}` } as any;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor to handle global 401 Unauthorized errors with transparent
// refresh-token rotation. If the refresh succeeds the original request is
// retried once with the new access token; otherwise the session is cleared
// and the app redirects to sign-in via the 'auth-unauthorized' event.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (!error.response) return Promise.reject(error);

    const isAuthEndpoint =
      original?.url?.includes('/auth/login') ||
      original?.url?.includes('/auth/refresh') ||
      original?.url?.includes('/auth/logout') ||
      original?.url?.includes('/auth/register');

    // The backend returns 401 for missing/expired/invalid tokens and 403 for
    // authorization failures. Both "token" flavored errors mean the session
    // needs refreshing — a stale access token must never dead-end the request.
    const isTokenError =
      error.response.status === 401 ||
      (error.response.status === 403 && typeof error.response.data?.error === 'string' && /token/i.test(error.response.data.error));

    // Only transparently refresh for real API calls that carried our token.
    if (isTokenError && original && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        if (original.headers && typeof original.headers.set === 'function') {
          original.headers.set('Authorization', `Bearer ${tokenStore.get()}`);
        } else {
          original.headers = { ...original.headers, Authorization: `Bearer ${tokenStore.get()}` } as any;
        }
        return api(original);
      }
      // If refresh failed, do NOT return here. Let the code fall through
      // to the stale state clearing block below!
    }

    // Handle 403 Forbidden for role-restricted resources (seller / admin)
    if (error.response.status === 403) {
      const errMessage = String(error.response.data?.error || '');
      const url = String(original?.url || '');
      if (/seller membership/i.test(errMessage) || url.includes('/seller')) {
        window.dispatchEvent(
          new CustomEvent('auth-role-updated', {
            detail: { isPromoted: false, reason: errMessage || 'Seller membership is revoked' },
          })
        );
      } else if (/admin/i.test(errMessage) || url.includes('/admin')) {
        window.dispatchEvent(
          new CustomEvent('auth-role-updated', {
            detail: { isAdmin: false, reason: errMessage || 'Admin access revoked' },
          })
        );
      }
    }

    // Login/register/refresh failures: clear stale state but only nuke the
    // token if one was actually present (so real login errors surface intact).
    if (error.response.status === 401 && tokenStore.get()) {
      clearAuthAndNotify();
    }
    return Promise.reject(error);
  }
);

export default api;
